import * as React from "react";
import { useState } from "react";
import {
  Button, FormControlLabel, Checkbox, Paper, Box, Grid, Typography, useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ValidatorForm, TextValidator } from "react-material-ui-form-validator";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { getDoc, doc, getDocs, collection, query, where } from "firebase/firestore";
import { db } from "../database/db";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ScaleLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../Context/authContext";
import { useDispatch } from "react-redux";
import { setAdminData } from "../store";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [auth, setAuth] = React.useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const handleLogin = async () => {
    if (!email || !password) {
      toast.error("Please fill all fields!");
      return;
    }
    setLoading(true);
    try {
      const firebaseAuth = getAuth();
      const userCredential = await signInWithEmailAndPassword(firebaseAuth, email, password);
      const user = userCredential.user;

      // Look up admin record in Firestore
      const adminSnap = await getDocs(
        query(collection(db, "AdminUser"), where("email", "==", email))
      );
      if (adminSnap.empty) {
        toast.error("No admin account found for this email");
        setLoading(false);
        return;
      }
      const adminData = { id: adminSnap.docs[0].id, ...adminSnap.docs[0].data() };

      const data = { userId: user.uid, email: user.email, ...adminData };
      localStorage.setItem("auth", JSON.stringify(data));
      setAuth(data);
      dispatch(setAdminData(adminData));
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error.message.replace("Firebase: ", ""));
    }
    setLoading(false);
  };

  return (
    <Grid
      container alignItems="center" justifyContent="center"
      sx={{ height: "100vh", background: "linear-gradient(135deg, #0e0e0e 0%, #1a1a1a 50%, #0e0e0e 100%)" }}
    >
      <ToastContainer position="top-right" />
      <Grid
        container component="main"
        sx={{ height: "80vh", width: { xs: "95%", sm: "80%", md: "65%" }, borderRadius: 3, overflow: "hidden", boxShadow: "0 24px 48px rgba(0,0,0,0.5)" }}
      >
        {/* Left panel */}
        <Grid
          item xs={false} md={6}
          sx={{
            background: "linear-gradient(135deg, #FF6C40 0%, #FF4842 50%, #c62828 100%)",
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
          }}
        >
          <Typography variant="h3" sx={{ color: "#fff", fontWeight: 900, mb: 1 }}>
            🔥
          </Typography>
          <Typography variant="h4" sx={{ color: "#fff", fontWeight: 800, textAlign: "center" }}>
            Fire Investing
          </Typography>
          <Typography variant="subtitle1" sx={{ color: "rgba(255,255,255,0.8)", textAlign: "center", mt: 1 }}>
            Admin Control Panel
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", textAlign: "center", mt: 3, maxWidth: 280 }}>
            Manage users, monitor FIV alerts, handle reports, and keep the community safe.
          </Typography>
        </Grid>

        {/* Right panel */}
        <Grid item xs={12} md={6} component={Paper} elevation={0}
          sx={{ bgcolor: "#181818", display: "flex", alignItems: "center" }}>
          <Box sx={{ width: "100%", px: { xs: 4, md: 6 }, py: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: "#fff" }}>
              Welcome back 👋
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Sign in to your admin account
            </Typography>

            <ValidatorForm onSubmit={handleLogin} onError={() => {}}>
              <TextValidator
                fullWidth variant="outlined" placeholder="Admin Email"
                onChange={(e) => setEmail(e.target.value)}
                name="email" value={email}
                validators={["required", "isEmail"]}
                errorMessages={["Email required", "Invalid email"]}
                sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextValidator
                fullWidth variant="outlined" placeholder="Password" type="password"
                onChange={(e) => setPassword(e.target.value)}
                name="password" value={password}
                validators={["required"]}
                errorMessages={["Password required"]}
                sx={{ mb: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                {loading ? (
                  <ScaleLoader color="#FF6C40" />
                ) : (
                  <Button type="submit" variant="contained" fullWidth
                    sx={{ py: 1.5, borderRadius: 2, bgcolor: "#FF6C40", "&:hover": { bgcolor: "#E05A30" }, fontWeight: 700, fontSize: 16 }}>
                    Sign In
                  </Button>
                )}
              </Box>
            </ValidatorForm>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
}
